import { useEffect, useRef } from "react";
import { toast } from "~/components/ui/use-toast";

// AIDEV-NOTE: Development-only component to monitor and report hydration errors
export function HydrationErrorMonitor() {
    const errorCountRef = useRef(0);
    const lastErrorRef = useRef<string>("");

    useEffect(() => {
        // Only run in development
        if (process.env.NODE_ENV !== "development") {
            return;
        }

        // Store original console.error
        const originalConsoleError = console.error;

        // Override console.error to catch hydration errors
        console.error = (...args) => {
            const errorMessage = args.join(" ");

            // Check for hydration-related errors
            const hydrationPatterns = [
                "Hydration failed",
                "There was an error while hydrating",
                "did not match",
                "Warning: Expected server HTML",
                "Text content does not match",
                "Prop `",
                "Extra attributes from the server",
                "Did not expect server HTML",
            ];

            const isHydrationError = hydrationPatterns.some((pattern) =>
                errorMessage.includes(pattern),
            );

            if (isHydrationError) {
                // Avoid duplicate toasts for the same error
                if (lastErrorRef.current !== errorMessage) {
                    errorCountRef.current++;
                    lastErrorRef.current = errorMessage;

                    // Extract the most relevant part of the error
                    let shortMessage = "Hydration mismatch detected";

                    // Try to extract component name or specific issue
                    if (
                        errorMessage.includes("Expected server HTML to contain")
                    ) {
                        const match = errorMessage.match(
                            /contain a matching <(\w+)>/,
                        );
                        if (match) {
                            shortMessage = `Hydration: Missing <${match[1]}> element`;
                        }
                    } else if (errorMessage.includes("did not match")) {
                        const match = errorMessage.match(
                            /Warning: (.+?) did not match/,
                        );
                        if (match) {
                            shortMessage = `Hydration: ${match[1]} mismatch`;
                        }
                    } else if (
                        errorMessage.includes("Text content does not match")
                    ) {
                        shortMessage = "Hydration: Text content mismatch";
                    }

                    // Extract component stack if available
                    const stackMatch = errorMessage.match(/at (\w+) \(/);
                    const componentName = stackMatch
                        ? stackMatch[1]
                        : "Unknown";

                    // Show toast with error details
                    toast({
                        title: `🚨 Hydration Error #${errorCountRef.current}`,
                        description: (
                            <div className="select-text space-y-1 text-xs">
                                <div className="font-medium">
                                    {shortMessage}
                                </div>
                                <div className="text-muted-foreground">
                                    Component: {componentName}
                                </div>
                                <details className="cursor-pointer">
                                    <summary className="text-muted-foreground hover:text-foreground">
                                        View full error
                                    </summary>
                                    <pre className="mt-2 max-h-32 overflow-auto rounded bg-muted p-2 text-xs">
                                        {errorMessage.substring(0, 500)}
                                    </pre>
                                </details>
                            </div>
                        ),
                        duration: 10000, // Show for 10 seconds
                        className: "!top-4 !left-4 !right-auto max-w-md",
                    });

                    // Also log to console for debugging
                    console.warn(
                        `[Hydration Monitor] Error #${errorCountRef.current} detected:`,
                        shortMessage,
                        "\nFull error:",
                        errorMessage,
                    );
                }
            }

            // Call original console.error
            originalConsoleError.apply(console, args);
        };

        // Cleanup on unmount
        return () => {
            console.error = originalConsoleError;
        };
    }, []);

    // No visual output - monitoring only
    return null;
}
